import axios, { AxiosError } from 'axios'
import { API_CONFIG, STORAGE_KEYS } from '@/config/env'

const apiClient = axios.create({
  baseURL: `${API_CONFIG.baseUrl.replace(/\/$/, '')}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
  withCredentials: true,
})

apiClient.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem(STORAGE_KEYS.authToken)
      if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    if (config.retry === undefined) {
      config.retry = 1
      config.retryDelay = 500
    }
    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config

    if (error.response?.status === 401 && typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.authToken)
      localStorage.removeItem(STORAGE_KEYS.refreshToken)
      window.location.href = '/login'
    }

    // Don't retry on 5xx errors (server-side issues)
    if (error.response?.status && error.response.status >= 500) {
      return Promise.reject(error)
    }

    if (!config || !config.retry) {
      return Promise.reject(error)
    }

    const currentRetryCount = ((config as any).__retryCount || 0) + 1
    ;(config as any).__retryCount = currentRetryCount

    if (currentRetryCount > config.retry) {
      return Promise.reject(error)
    }

    await new Promise((resolve) => setTimeout(resolve, config.retryDelay ?? 500))
    return apiClient(config)
  }
)

export const withAbortSignal = () => {
  const controller = new AbortController()
  return {
    signal: controller.signal,
    dispose: () => controller.abort(),
  }
}

export default apiClient

